import type { TWebshop } from '@common/ShoprenterAPI/definitions'

/** BASE GET REQUEST. */
export type TGetType = 'full' | 'digest'
export interface IBaseGetRequest {
  readonly type: TGetType
  readonly startDate: string
  readonly endDate: string
  readonly webshop?: string
}

export interface IDigestData {
  date: string
  occurrence: number
}

/** PriceUpdater. */
export interface IBaseProduct {
  readonly sku: string
  readonly oldPrice: string
  readonly newPrice: string
}

export interface IProductPriceUpdaterRequest extends IBaseProduct {
  readonly webshop: string
}

export interface IProductPriceUpdateInsert extends IBaseProduct {
  readonly webshopId: number

}
export interface IProductPriceUpdate extends IProductPriceUpdaterRequest {
  readonly createdAt: string

}
export interface IPriceUpdaterResponse {
  readonly taneszkoz: IDigestData[]
  readonly bdovi: IDigestData[]
}

export type TPriceUpdaterResponse = IPriceUpdaterResponse | IProductPriceUpdate[]

export type TPriceUpdaterData = IProductPriceUpdaterRequest | IBaseGetRequest

/** MisssingProducts. */
export interface IBaseMissingProducts {
  readonly all: number
  readonly errors: number
  readonly warnings: number
  readonly infos: number
  readonly notices: number
}

export interface IMissingProcuctsRequest extends IBaseMissingProducts {
  readonly webshop: string
}

export interface IGetMissingProductsRequest extends IBaseGetRequest {}

export interface IMissingProduct extends IMissingProcuctsRequest {
  readonly date: string
}

export type TMissingProducts = readonly IMissingProduct[]

export interface IMissingProductItem {
  readonly date: string
  readonly missingProducts: IBaseMissingProducts | null
}

export type TMissingProductsDigestResponse = IMissingProductItem[]

export type TMissingProductsResponse = TMissingProductsDigestResponse | TMissingProducts

export type TMissingProductsData = IMissingProcuctsRequest | IGetMissingProductsRequest

/** CodeService. */
export type TCSCommand = 'activate' | 'update' | 'deactivate'
export interface IActivityRequest {
  readonly route: TCSCommand
  readonly params: string
  readonly outcome: string
}
export interface IGetActivitiesRequest extends IBaseGetRequest {
  readonly route: TCSCommand
}
export interface IActivity extends IActivityRequest {
  readonly createdAt: string
}

export type TActivities = readonly IActivity[]
export type TCodeServiceData = IActivityRequest | IGetActivitiesRequest

export interface ICodeServiceDigest {
  readonly activate: IDigestData[]
  readonly update: IDigestData[]
  readonly deactivate: IDigestData[]
}

export type TCodeServiceResponse = ICodeServiceDigest | TActivities

export type TDigestResponse = readonly IDigestData[]

/** WrongProducts. */
export interface IBaseWrongProduct {
  readonly wrongDescriptions: number
  readonly missingPictures: number
  readonly wrongPictures: number
  readonly missingCategories: number
  readonly missingPrices: number
}

export interface IWrongProductsRequest extends IBaseWrongProduct {
  readonly webshop: string
}

export interface IWrongProductModel extends IBaseWrongProduct {
  readonly date: string
  readonly webshopId: number
  readonly webshopName: string
  readonly createdAt: string
}

export interface IWrongProductItem {
  readonly date: string
  readonly wrongProducts: IBaseWrongProduct | null
}

export type TWrongProductsData = IWrongProductsRequest | IBaseGetRequest
export type TWrongProductsResponse = IWrongProductModel[] | IWrongProductItem[]

export interface IBaseRunTimeErrorData {
  readonly project:      string
  readonly stack:        string
  readonly message:      string
  readonly resolution:   string
  readonly userAgent:    string
}

/** Runtime Errors. */
export interface IRuntimeErrorData extends IBaseRunTimeErrorData {
  readonly columnNumber: number
  readonly lineNumber:   number
}

export interface IRuntimeErrorModel extends IRuntimeErrorData {
  readonly createdAt: string
}

export interface ILastRunTimeErrorData extends IRuntimeErrorData {
  readonly date: number
}

export interface IProjectErrOccurence {
  readonly project: string
  readonly count: number
}

export type TProjectErrOccurences = readonly IProjectErrOccurence[]

export type TRuntimeErrors = readonly IRuntimeErrorModel[]

export type TRuntTimeErrorRequestTypes = 'getall' | 'topten' | 'daily'

export interface IGetRuntimeErrors {
  readonly limit: number
}

export interface IBaseGetErrors {
  readonly startDate: string
  readonly endDate: string
}

export type TRuntimeErrorRequestDetails = IGetRuntimeErrors | IBaseGetErrors
export interface IRuntimeGetErrorsRequest {
  readonly requestType: TRuntTimeErrorRequestTypes
  readonly requestDetails: TRuntimeErrorRequestDetails
}

export type TRuntimeErrorData = IRuntimeErrorData | IRuntimeGetErrorsRequest
export type TRuntimeErrorResponse = TRuntimeErrors | TProjectErrOccurences

/** DeployService. */
export interface IDeployServiceData {
  readonly date: string
  readonly count: number
}

export type TDeployServiceData = readonly IDeployServiceData[]

/** PromoCodeService. */
export interface IPromoCodeServiceGetData {
  readonly quantity?: number
}

export interface IPromoCodeServiceData {
  readonly codeId: number
}

export interface IPromoCodeServiceResponseData {
  readonly codeId: number
  readonly firstUse: string | null
  readonly lastUse: string | null
  readonly useCount: number
}

export type TPromoCodeServiceResponseData = readonly IPromoCodeServiceResponseData[]

/** ShoprenterService. */

export enum EShoprenterServiceRequestType {
  Pending = 1,
  Upload = 2
}

export enum EShoprenterServiceSourceType {
  Manual = 'manual',
  Excel = 'excel'
}

export interface IShoprenterServiceInsertRequest {
  /** "pending" vagy ténylegesen feltöltésre került e. */
  readonly requestType: EShoprenterServiceRequestType

  /** Ha "pending", akkor megmondja, hogy melyik forrásból. */
  readonly source?: EShoprenterServiceSourceType

  /** Ha pedig feltöltés, akkor megmondja, melyik webshop. */
  readonly webshop?: TWebshop

  /** Hány darab. */
  readonly count: number
}

export interface IBaseShoprenterServiceItem {
  manualCount: number
  excelCount: number
  bdoviCount: number
  taneszkozCount: number

}
export interface IShoprenterServiceItem extends IBaseShoprenterServiceItem {
  readonly date: string
}

export interface IShoprenterServiceGetItem {
  readonly date: string
  readonly pendingCount: number
  readonly uploadCount: number
}

export type TShoprenterServiceData = IShoprenterServiceInsertRequest

export type TShoprenterServiceResponse = IShoprenterServiceGetItem[]

/** Ügyesedni apps. */

export type TUgyesedniStartType = 'web' | 'cordova' | 'electron'

export interface IBaseUgyesedniAppItem {
  /** Az app szériaszáma: BD-TO0102 . */
  readonly serial: string

  /** Az indítási típus. */
  readonly startType: TUgyesedniStartType

  /** Milyen IP-ről jött? */
  readonly ip?: string

  /**
   * A kliens által küldött useragent.
   * @see {@link https://developer.mozilla.org/en-US/docs/Web/API/Navigator/userAgent}
   */
  readonly userAgent?: string

  /**
   * Kliens által küldött referrer.
   * @see {@link https://developer.mozilla.org/en-US/docs/Web/API/Document/referrer}
   */
  readonly referrer?: string

  /** A felbontás, amiben játszott. */
  readonly resolution?: string
}

interface IUgyesedniActivity extends IBaseUgyesedniAppItem {
  readonly createdAt: string
}

export type TUgyesedniActivities = readonly IUgyesedniActivity[]

export interface IUgyesedniStatistics {
  readonly serial: string
  readonly count: number
}

interface IUgyesedniStatisticsData {
  readonly count: number
}

export type TUgyesedniStatistics = readonly IUgyesedniStatistics[]

type TUgyesedniAppsData = IBaseUgyesedniAppItem

export const PROJECTS = [
  'codeService',
  'deployService',
  'missingProducts',
  'priceUpdater',
  'promoCodeService',
  'runtimeErrors',
  'shoprenterService',
  'ugyesedniApps',
  'wrongProducts'
] as const

// Milyen projektek vannak.
export type TProjectType = typeof PROJECTS[number]

// Milyen response lehet.
export type TResponse = (
  TCodeServiceResponse |
  TDeployServiceData |
  TDigestResponse |
  TMissingProductsResponse |
  TPriceUpdaterResponse |
  TPromoCodeServiceResponseData |
  TRuntimeErrorResponse |
  TShoprenterServiceResponse |
  TWrongProductsResponse |
  TUgyesedniStatistics
)

// Milyen bejövő adat lehet.
export type TData = (
  TCodeServiceData |
  TMissingProductsData |
  TPriceUpdaterData |
  IPromoCodeServiceGetData |
  TRuntimeErrorData |
  IPromoCodeServiceData |
  TShoprenterServiceData |
  TUgyesedniAppsData |
  TWrongProductsData |
  IUgyesedniStatisticsData
)

export interface IRequest {
  readonly project: TProjectType
  readonly data: TData
}

export enum EStatRoute  {
  Insert = '/api/insert',
  Get    = '/api/get'
}

export interface IWebshop {
  readonly id: number
  readonly name: string
}

export type TCmdType = 'create' | 'read'

export type THandler = (cmd: TCmdType, data: TData) => Promise<TResponse | null>

export interface IHandler {
  readonly project: TProjectType
  readonly handler: THandler
}
